import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { Printer, AlertCircle, Copy, CreditCard, Building2, Check, Smartphone, Calendar, ExternalLink, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function PublicProforma() {
    const { t } = useTranslation();
    const { token } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [stripeLoading, setStripeLoading] = useState(false);

    useEffect(() => {
        let active = true;
        (async () => {
            try {
                const res = await axios.get(`${BACKEND_URL}/api/public/proforme/${token}`);
                if (active) setData(res.data);
            } catch (e) {
                if (active) setError(e.response?.status === 404 ? 'not_found' : 'error');
            } finally {
                if (active) setLoading(false);
            }
        })();
        return () => { active = false; };
    }, [token]);

    // After Stripe redirect with ?session_id=...
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const sid = params.get('session_id');
        if (!sid) return;
        let cancelled = false;
        const poll = async (attempts = 0) => {
            if (cancelled || attempts > 6) return;
            try {
                const r = await axios.get(`${BACKEND_URL}/api/payments/proforma/status/${sid}`);
                if (r.data.payment_status === 'paid') {
                    toast.success('Pagamento ricevuto! Grazie.');
                    // Refresh proforma data
                    const fresh = await axios.get(`${BACKEND_URL}/api/public/proforme/${token}`);
                    setData(fresh.data);
                    // Clean URL
                    window.history.replaceState({}, '', window.location.pathname);
                    return;
                }
                setTimeout(() => poll(attempts + 1), 2000);
            } catch (_) { /* keep polling */ }
        };
        poll();
        return () => { cancelled = true; };
    }, [token]);

    const payWithStripe = async () => {
        setStripeLoading(true);
        try {
            const r = await axios.post(`${BACKEND_URL}/api/payments/proforma/checkout`, {
                share_token: token,
                origin_url: window.location.origin,
            });
            window.location.href = r.data.url;
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Errore pagamento');
            setStripeLoading(false);
        }
    };

    const copyIban = async () => {
        try {
            await navigator.clipboard.writeText(data.iban);
            toast.success('IBAN copiato');
        } catch (_) {
            toast.error('Errore copia');
        }
    };
    const copyAmount = async () => {
        try {
            await navigator.clipboard.writeText(String(data.importo));
            toast.success('Importo copiato');
        } catch (_) {
            toast.error('Errore copia');
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-[hsl(var(--background))] py-8 px-4">
                <div className="max-w-2xl mx-auto space-y-4">
                    <Skeleton className="h-12 w-1/2" />
                    <Skeleton className="h-40 w-full" />
                    <Skeleton className="h-60 w-full" />
                </div>
            </div>
        );
    }

    if (error || !data) {
        return (
            <div className="min-h-screen bg-[hsl(var(--background))] flex items-center justify-center px-4">
                <Card className="p-8 max-w-md text-center">
                    <AlertCircle className="w-12 h-12 mx-auto mb-3 text-[hsl(0_72%_52%)]" />
                    <h2 className="font-display text-2xl mb-2">Proforma non trovata</h2>
                    <p className="text-sm text-[hsl(var(--muted-foreground))]">Il link potrebbe non essere più valido.</p>
                </Card>
            </div>
        );
    }

    const isPagata = data.stato === 'pagata';
    const importoFmt = Number(data.importo || 0).toLocaleString('it-IT', { minimumFractionDigits: 2 });

    return (
        <div className="min-h-screen bg-[hsl(var(--background))] py-6 px-4" data-testid="public-proforma">
            <div className="max-w-2xl mx-auto">
                {/* Header */}
                <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                    <div>
                        <h1 className="font-display text-3xl md:text-4xl">PROFORMA ANTICIPO</h1>
                        <div className="text-sm text-[hsl(var(--muted-foreground))] mt-1">{data.numero} · Rif. preventivo {data.preventivo_numero}</div>
                    </div>
                    <div className="flex items-center gap-2">
                        {isPagata ? (
                            <Badge className="bg-[hsl(145_55%_34%)] text-white"><Check className="w-3 h-3 mr-1" /> PAGATA</Badge>
                        ) : (
                            <Badge className="bg-[hsl(45_100%_92%)] text-[hsl(35_90%_35%)]">DA PAGARE</Badge>
                        )}
                        <Button onClick={() => window.print()} variant="outline" size="sm" className="gap-1 no-print"><Printer className="w-4 h-4" /> Stampa</Button>
                    </div>
                </div>

                {/* Hero importo */}
                <Card className={`p-6 mb-4 ${isPagata ? 'border-[hsl(145_55%_34%)]/40 bg-[hsl(145_45%_98%)]' : 'border-[hsl(var(--accent))]/40 bg-[hsl(var(--accent))]/5'}`}>
                    <div className="text-center">
                        <div className="text-xs uppercase tracking-widest text-[hsl(var(--muted-foreground))] mb-2">Importo anticipo {data.anticipo_pct}%</div>
                        <div className="font-display text-5xl md:text-6xl tabular-nums" data-testid="proforma-importo-hero">€{importoFmt}</div>
                        <div className="text-sm text-[hsl(var(--muted-foreground))] mt-2">su totale lavoro €{Number(data.totale_preventivo || 0).toLocaleString('it-IT', { minimumFractionDigits: 2 })}</div>
                        {!isPagata && (
                            <Button onClick={copyAmount} variant="outline" size="sm" className="mt-3 gap-1 no-print"><Copy className="w-3 h-3" /> Copia importo</Button>
                        )}
                    </div>
                </Card>

                {/* Lavoro */}
                <Card className="p-5 mb-4">
                    <div className="text-xs uppercase tracking-widest text-[hsl(var(--muted-foreground))] mb-1">Cliente</div>
                    <div className="font-semibold text-lg mb-3">{data.cliente}</div>
                    <div className="text-xs uppercase tracking-widest text-[hsl(var(--muted-foreground))] mb-1">Lavoro</div>
                    <div className="text-sm">{data.titolo}</div>
                </Card>

                {/* Paga online (Stripe + PayPal / Satispay / Rateizza) */}
                {!isPagata && (() => {
                    const commCliente = data.commissioni_a_carico === 'cliente';
                    const commPct = parseFloat(data.commissioni_pct || 0);
                    const importoCarta = commCliente && commPct > 0
                        ? Number(data.importo) * (1 + commPct / 100)
                        : Number(data.importo);
                    const importoCartaFmt = importoCarta.toLocaleString('it-IT', { minimumFractionDigits: 2 });
                    const paypalUrl = data.paypal_me && (data.paypal_me.startsWith('http') ? `${data.paypal_me}/${importoCarta.toFixed(2)}EUR` : `https://paypal.me/${data.paypal_me}/${importoCarta.toFixed(2)}EUR`);
                    return (
                        <Card className="p-5 mb-4 border-[hsl(var(--accent))]/30">
                            <h3 className="font-display text-xl flex items-center gap-2 mb-3 pb-2 border-b border-[hsl(var(--border))]">
                                <CreditCard className="w-5 h-5 text-[hsl(var(--accent))]" /> {t('pages.pubproforma_pay_title') || 'Paga online'}
                            </h3>
                            {commCliente && commPct > 0 && (
                                <div className="mb-3 p-2 rounded-md bg-[hsl(45_100%_92%)] text-[hsl(35_90%_35%)] text-xs">
                                    {t('pages.pubproforma_pay_commissioni') || 'Le commissioni del pagamento online verranno aggiunte all\'importo finale.'}
                                    <div className="mt-1 font-semibold">{t('pages.pubproforma_pay_importo_carta') || 'Importo con commissioni'}: <span className="tabular-nums">€{importoCartaFmt}</span> (+{commPct}%)</div>
                                </div>
                            )}
                            {(!commCliente || commPct === 0) && (
                                <div className="mb-3 text-xs text-[hsl(145_55%_34%)]">
                                    ✓ {t('pages.pubproforma_pay_no_commissioni') || 'Nessuna commissione aggiuntiva per il pagamento online.'}
                                </div>
                            )}
                            <div className="grid gap-2">
                                {/* PRIMARY: Stripe (cards + Apple Pay + Google Pay) - always available */}
                                <Button onClick={payWithStripe} disabled={stripeLoading} className="bg-[hsl(220_13%_10%)] hover:bg-[hsl(220_13%_18%)] text-white h-14 gap-2 font-bold text-base" data-testid="proforma-stripe-button">
                                    {stripeLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <CreditCard className="w-5 h-5" />}
                                    {t('pages.pubproforma_pay_card') || 'Paga con carta'} · Apple Pay · Google Pay
                                </Button>
                                <div className="grid sm:grid-cols-2 gap-2">
                                    {data.paypal_me && (
                                        <Button asChild className="bg-[#0070BA] hover:bg-[#005a96] text-white h-12 gap-2 font-bold" data-testid="proforma-paypal-button">
                                            <a href={paypalUrl} target="_blank" rel="noopener noreferrer">
                                                <span className="inline-block w-6 h-6 rounded bg-white text-[#0070BA] text-xs font-bold flex items-center justify-center">P</span>
                                                {t('pages.pubproforma_pay_paypal') || 'Paga con PayPal'}
                                                <ExternalLink className="w-3 h-3 ml-1" />
                                            </a>
                                        </Button>
                                    )}
                                    {data.satispay_link && (
                                        <Button asChild className="bg-[#E91E50] hover:bg-[#c1183f] text-white h-12 gap-2 font-bold" data-testid="proforma-satispay-button">
                                            <a href={data.satispay_link} target="_blank" rel="noopener noreferrer">
                                                <Smartphone className="w-4 h-4" />
                                                {t('pages.pubproforma_pay_satispay') || 'Paga con Satispay'}
                                                <ExternalLink className="w-3 h-3 ml-1" />
                                            </a>
                                        </Button>
                                    )}
                                </div>
                                {data.rateizza_attivo && (
                                    <Button asChild variant="outline" className="h-12 gap-2 border-[hsl(var(--accent))] text-[hsl(220_13%_10%)] font-semibold" data-testid="proforma-rateizza-button">
                                        <a href={`mailto:?subject=Richiesta rateizzazione ${data.numero}&body=Vorrei informazioni sulla rateizzazione della proforma ${data.numero} per l'importo di €${importoCartaFmt}.`}>
                                            <Calendar className="w-4 h-4" />
                                            {t('pages.pubproforma_pay_rateizza') || 'Rateizza'}
                                            {data.rateizza_info && <span className="text-xs font-normal opacity-70 ml-2 truncate hidden sm:inline">— {data.rateizza_info}</span>}
                                        </a>
                                    </Button>
                                )}
                            </div>
                            <div className="mt-3 text-xs text-[hsl(var(--muted-foreground))] flex items-start gap-1">
                                <Check className="w-3 h-3 mt-0.5 text-[hsl(145_55%_34%)]" />
                                <span>{t('pages.pubproforma_pay_auto') || 'I pagamenti con carta vengono confermati automaticamente in tempo reale.'}</span>
                            </div>
                        </Card>
                    );
                })()}

                {/* Dati bancari */}
                {!isPagata && data.iban && (
                    <Card className="p-5 mb-4 border-[hsl(var(--border))]">
                        <h3 className="font-display text-xl flex items-center gap-2 mb-3 pb-2 border-b border-[hsl(var(--border))]">
                            <Building2 className="w-5 h-5 text-[hsl(var(--accent))]" /> {t('pages.pubproforma_pay_bonifico') || 'Bonifico bancario'}
                        </h3>
                        <div className="space-y-3">
                            <div>
                                <div className="text-xs text-[hsl(var(--muted-foreground))] mb-1">Beneficiario</div>
                                <div className="font-semibold flex items-center gap-2"><Building2 className="w-4 h-4" /> {data.beneficiario}</div>
                                {data.piva_beneficiario && <div className="text-xs text-[hsl(var(--muted-foreground))]">P.IVA {data.piva_beneficiario}</div>}
                            </div>
                            <div>
                                <div className="text-xs text-[hsl(var(--muted-foreground))] mb-1">IBAN</div>
                                <div className="flex items-center gap-2 p-3 rounded-lg bg-[hsl(var(--muted))] font-mono text-sm tracking-wider break-all">
                                    <span className="flex-1">{data.iban}</span>
                                    <Button onClick={copyIban} variant="ghost" size="sm" className="h-8 px-2 no-print" data-testid="proforma-copy-iban"><Copy className="w-3 h-3" /></Button>
                                </div>
                            </div>
                            {data.banca_nome && (
                                <div>
                                    <div className="text-xs text-[hsl(var(--muted-foreground))] mb-1">Banca</div>
                                    <div>{data.banca_nome}</div>
                                </div>
                            )}
                            {data.bic_swift && (
                                <div>
                                    <div className="text-xs text-[hsl(var(--muted-foreground))] mb-1">BIC/SWIFT</div>
                                    <div className="font-mono">{data.bic_swift}</div>
                                </div>
                            )}
                            <div>
                                <div className="text-xs text-[hsl(var(--muted-foreground))] mb-1">Causale del bonifico (importante)</div>
                                <div className="p-3 rounded-lg bg-[hsl(45_100%_92%)] text-[hsl(35_90%_35%)] font-medium text-sm">
                                    Anticipo {data.anticipo_pct}% rif. {data.numero} - {data.preventivo_numero}
                                </div>
                            </div>
                        </div>
                    </Card>
                )}

                {isPagata && (
                    <Card className="p-5 mb-4 border-[hsl(145_55%_34%)]/40 bg-[hsl(145_45%_98%)] text-center">
                        <Check className="w-12 h-12 mx-auto mb-3 text-[hsl(145_55%_34%)]" />
                        <h3 className="font-display text-2xl text-[hsl(145_55%_34%)]">Pagamento ricevuto!</h3>
                        <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2">Grazie. I lavori partiranno secondo accordi.</p>
                        {data.data_pagamento && <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">Confermato il {new Date(data.data_pagamento).toLocaleDateString('it-IT')}</p>}
                    </Card>
                )}

                {data.note && (
                    <Card className="p-4 mb-4 text-sm text-[hsl(var(--muted-foreground))]">
                        <strong>Note:</strong> {data.note}
                    </Card>
                )}

                <div className="text-center text-xs text-[hsl(var(--muted-foreground))] mt-6">
                    Powered by CantiereAI
                </div>
            </div>
        </div>
    );
}
